import React, { useEffect, useState } from 'react';
import { ActionButton, Buttons, CancelButton, Drawer, Table } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import AccountSelect from './components/AccountSelect';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';

export default function ExpenseCategoryMappings({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const { qboConnectionId } = useParams();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);

  const [{ categories, accounts, isReady }, setState] = useState({ categories: null, isReady: false });

  useEffect(() => {
    (async () => {
      const { data: categories } = await api.www
        .workspaces(workspace.id)
        .qbo(qboConnectionId)
        .getExpenseCategoryMappings();
      const { data: accounts } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getExpenseAccounts();

      if (!isMounted.current) return;
      setState({ categories, accounts, isReady: true });
    })();
  }, [api, workspace.id, isMounted, qboConnectionId]);

  const updateCategoryMapping = ({ expenseCategoryId, qboAccountId }) => {
    setDirty(true);

    setState((state) => ({
      ...state,
      categories: state.categories.map((c) => (c.id === expenseCategoryId ? { ...c, qboAccountId } : c)),
    }));
  };

  return (
    <Drawer
      isOpen
      title="Expense Category Mappings"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();

          try {
            const mappings = categories.map((category) => ({
              expenseCategoryId: category.id,
              qboAccountId: category.qboAccountId,
            }));
            await api.www.workspaces(workspace.id).qbo(qboConnectionId).updateExpenseCategoryMappings(mappings);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        if (!isReady) return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="40%">Expense Category</Table.Column>

                <Table.Column>
                  <strong>QuickBooks Expense Account</strong>
                </Table.Column>
              </Table.BoxHeader>

              <Table.Body>
                {categories.map((category) => {
                  const handleAccountChange = (event) => {
                    updateCategoryMapping({
                      expenseCategoryId: category.id,
                      qboAccountId: event.target.value || null,
                    });
                  };

                  return (
                    <Table.BoxRow key={category.id}>
                      <Table.Cell>{category.name}</Table.Cell>
                      <Table.Cell>
                        <AccountSelect
                          accounts={accounts}
                          value={category.qboAccountId}
                          onChange={handleAccountChange}
                        />
                      </Table.Cell>
                    </Table.BoxRow>
                  );
                })}
              </Table.Body>
            </Table>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}
